import { useState } from "react";
import { toast } from "sonner";
import { Check, Eye, Link2, MessageCircle, Send, X } from "lucide-react";
import { api, brl, errMsg, STATUS } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";

const FILTERS = [["", "Todos"], ["awaiting_confirmation", "Comprovante enviado"], ["pending", "Aguardando"], ["approved", "Pagos"], ["rejected", "Recusados"]];

export const PanelOrders = () => {
  const [status, setStatus] = useState("");
  const { data, reload } = useFetch(`/panel/orders?status=${status}`, []);
  const orders = Array.isArray(data) ? data : [];

  const act = async (id, action) => {
    try {
      await api.post(`/panel/orders/${id}/action`, { action });
      toast.success("Pedido atualizado");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const proof = async (id) => {
    try {
      const r = await api.get(`/panel/orders/${id}/proof`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      toast.error("Não foi possível abrir o comprovante");
    }
  };
  const link = (o) => `${window.location.origin}${o.link}`;

  return (
    <div className="space-y-6" data-testid="panel-orders">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`orders-filter-${k || "all"}`}
            className={`rounded-full border px-4 py-2 text-sm ${status === k ? "border-flash bg-flash text-ink" : "border-white/15"}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-3">
        {orders.map((o) => {
          const wa = o.phone ? `https://wa.me/${o.phone.replace(/\D/g, "").replace(/^(\d{10,11})$/, "55$1")}?text=${encodeURIComponent(`Olá ${o.name}! Seu pedido Eutevila: ${link(o)}`)}` : null;
          return (
            <div key={o.id} className="rounded-2xl border border-white/10 bg-ink-2 p-5" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{o.name} <span className="font-mono text-xs text-haze">#{o.id.slice(-6).toUpperCase()}</span></p>
                  <p className="text-sm text-haze">{o.email}{o.phone && ` · ${o.phone}`}</p>
                  <p className="mt-1 text-sm">{o.description}</p>
                  {o.booking && <p className="mt-1 text-xs text-haze">Data {o.booking.date} · {o.booking.event_type} · {o.booking.location} {o.booking.message && `· "${o.booking.message}"`}</p>}
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-bold">{brl(o.amount_cents)}</p>
                  <span className={`mt-1 inline-flex rounded-full border px-3 py-0.5 text-xs ${STATUS[o.status]?.tone}`}>{STATUS[o.status]?.label}</span>
                  <p className="mt-1 text-xs text-haze">{o.method === "pix" ? "Pix" : "Mercado Pago"}{o.delivered ? " · enviado" : ""}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {o.has_proof && <button onClick={() => proof(o.id)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`order-proof-${o.id}`}><Eye className="h-4 w-4" />Comprovante</button>}
                {o.status !== "approved" && <button onClick={() => act(o.id, "approve")} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-3 py-1.5 text-sm font-semibold text-ink" data-testid={`order-approve-${o.id}`}><Check className="h-4 w-4" />Confirmar pagamento</button>}
                {o.status !== "approved" && o.status !== "rejected" && <button onClick={() => act(o.id, "reject")} className="inline-flex items-center gap-1.5 rounded-full border border-red-400/40 px-3 py-1.5 text-sm text-red-300" data-testid={`order-reject-${o.id}`}><X className="h-4 w-4" />Recusar</button>}
                {o.status === "approved" && !o.delivered && o.kind === "photos" && <button onClick={() => act(o.id, "sent")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`order-sent-${o.id}`}><Send className="h-4 w-4" />Marcar como enviado</button>}
                <button onClick={() => { navigator.clipboard?.writeText(link(o)); toast.success("Link do pedido copiado"); }} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`order-copy-link-${o.id}`}><Link2 className="h-4 w-4" />Copiar link</button>
                {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`order-whatsapp-${o.id}`}><MessageCircle className="h-4 w-4" />WhatsApp</a>}
              </div>
            </div>
          );
        })}
        {!orders.length && <p className="text-haze" data-testid="panel-orders-empty">Nenhum pedido por aqui.</p>}
      </div>
    </div>
  );
};
